import type { CharacterProfile, ChatExportFields } from "@shared/schema";
import type { APIKeyConfig } from "@/types/api-key";
import type { ProviderResponse } from "@/types/usage";
import { parseJsonLike } from './json-utils';
import { appTitle } from "@/config/feature-flags";
import { parseOpenAIUsage } from "./usage-helpers";

function extractJsonObject(text: string): string {
  let t = text.trim();
  const codeBlock = t.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  if (codeBlock) t = codeBlock[1].trim();
  const jsonMatch = t.match(/(\{[\s\S]*\})/);
  if (jsonMatch) t = jsonMatch[1];
  return t;
}

function extractDataUriFromString(value: string): string | null {
  if (!value) return null;
  const direct = value.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
  if (direct) return direct[0];
  const markdown = value.match(/!\s*\[[^\]]*\]\((data:image\/\w+;base64,[^)]+)\)/i);
  if (markdown) return markdown[1];
  return null;
}

const OPENROUTER_SAFETY_REASONS = new Set([
  "IMAGE_SAFETY",
  "SAFETY",
  "PROHIBITED_CONTENT",
  "CONTENT_FILTER",
  "CONTENT_FILTERED",
  "BLOCKLIST",
  "BLOCKED",
  "POLICY_VIOLATION",
  "POLICY_VIOLATION_CONTENT",
  "TOXICITY",
]);

function coerceReason(value: unknown): string | undefined {
  if (!value) return undefined;
  const str = String(value).trim();
  if (!str) return undefined;
  return str.toUpperCase();
}

function getOpenRouterProviderName(json: any): string {
  const candidates = [
    json?.provider,
    json?.metadata?.provider,
    json?.choices?.[0]?.provider,
    json?.choices?.[0]?.metadata?.provider,
    json?.model,
    json?.choices?.[0]?.model,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return "The selected provider";
}

function extractRefusalDetail(refusal: unknown): string | undefined {
  if (!refusal) return undefined;
  if (typeof refusal === "string") {
    return refusal.trim() || undefined;
  }
  if (typeof refusal === "object") {
    const reason = (refusal as any)?.reason;
    const message = (refusal as any)?.message;
    const explanation = (refusal as any)?.explanation;
    const detail = [reason, message, explanation].find(
      (value) => typeof value === "string" && value.trim().length > 0
    );
    if (detail) {
      return String(detail).trim();
    }
  }
  return undefined;
}

function buildOpenRouterImageFailureMessage(json: any, fallback: string): string {
  const choice = Array.isArray(json?.choices) ? json.choices[0] : undefined;
  const providerName = getOpenRouterProviderName(json);

  const reasonCandidates = [
    choice?.native_finish_reason,
    choice?.nativeFinishReason,
    choice?.finish_reason,
    choice?.finishReason,
    json?.native_finish_reason,
    json?.nativeFinishReason,
    json?.finish_reason,
    choice?.metadata?.native_finish_reason,
    choice?.metadata?.finish_reason,
  ];
  const normalizedReasons = reasonCandidates
    .map((value) => coerceReason(value))
    .filter((value): value is string => Boolean(value));

  const safetyReason = normalizedReasons.find((reason) => OPENROUTER_SAFETY_REASONS.has(reason));
  if (safetyReason) {
    return `${providerName} rejected the image due to safety filters (${safetyReason}). Adjust your prompt to remove sensitive content and try again.`;
  }

  const refusalDetail = extractRefusalDetail(choice?.message?.refusal);
  if (refusalDetail) {
    return `${providerName} declined to create this image: ${refusalDetail}`;
  }

  if (normalizedReasons.length > 0) {
    const primaryReason = normalizedReasons[0];
    return `${providerName} did not return image data (finish reason: ${primaryReason}). Try adjusting your prompt or switch to another provider.`;
  }

  return fallback;
}

function isLikelyBareBase64(value: string): boolean {
  if (!value || value.length < 200) return false;
  const normalized = value.replace(/\s+/g, "");
  return /^[A-Za-z0-9+/]+={0,2}$/.test(normalized);
}

async function blobToDataUri(blob: Blob): Promise<string> {
  const mime = blob.type || "image/png";
  if (typeof FileReader !== "undefined") {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error || new Error("Failed to read blob"));
      reader.readAsDataURL(blob);
    });
  }
  const buffer = await blob.arrayBuffer();
  const base64 = arrayBufferToBase64(buffer);
  return `data:${mime};base64,${base64}`;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  if (typeof btoa === "function") {
    return btoa(binary);
  }
  if (typeof Buffer !== "undefined") {
    return Buffer.from(binary, "binary").toString("base64");
  }
  throw new Error("No base64 encoder available");
}

async function fetchUrlToDataUri(url: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const resp = await fetch(url, { signal });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await blobToDataUri(blob);
  } catch {
    return null;
  }
}

async function fetchOpenRouterAsset(
  apiKey: string,
  pointer: string,
  signal?: AbortSignal,
  visitedPointers?: Set<string>
): Promise<string | null> {
  const normalized = String(pointer || "").trim();
  if (!normalized) return null;
  if (visitedPointers && visitedPointers.has(normalized)) return null;
  visitedPointers?.add(normalized);
  const assetId = normalized.replace(/^asset:\/\//i, "").replace(/^assets\//i, "").trim();
  if (!assetId) return null;
  const endpoint = `https://openrouter.ai/api/v1/assets/${encodeURIComponent(assetId)}`;
  try {
    const resp = await fetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "HTTP-Referer": window.location.origin,
        "X-Title": appTitle,
      },
      signal,
    });
    if (!resp.ok) {
      return null;
    }
    const contentType = resp.headers.get("content-type") || "";
    if (contentType.toLowerCase().startsWith("image/")) {
      const blob = await resp.blob();
      return await blobToDataUri(blob);
    }
    if (contentType.includes("application/json")) {
      const assetJson = await resp.json();
      const base64Candidates = [
        assetJson?.data?.b64_json,
        assetJson?.data?.base64,
        assetJson?.data?.image_base64,
        assetJson?.b64_json,
        assetJson?.base64,
        assetJson?.image_base64,
      ];
      for (const candidate of base64Candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 100) {
          return `data:image/png;base64,${candidate.trim()}`;
        }
      }
      const urlCandidates = [
        assetJson?.data?.url,
        assetJson?.data?.signed_url,
        assetJson?.url,
        assetJson?.signed_url,
      ];
      for (const candidate of urlCandidates) {
        if (typeof candidate === "string" && candidate.trim()) {
          const resolved = await fetchUrlToDataUri(candidate.trim(), signal);
          if (resolved) return resolved;
        }
      }
      const nested = assetJson?.data;
      if (nested && typeof nested === "object") {
        const nestedResolved = await findImageInValue(nested, {
          apiKey,
          signal,
          visited: new Set<any>(),
          visitedPointers: visitedPointers ?? new Set<string>(),
        });
        if (nestedResolved) return nestedResolved;
      }
      return null;
    }
    const blob = await resp.blob();
    return await blobToDataUri(blob);
  } catch {
    return null;
  }
}

type OpenRouterImageContext = {
  apiKey: string;
  signal?: AbortSignal;
  visited: Set<any>;
  visitedPointers: Set<string>;
};

async function normalizeOpenRouterImageString(
  value: string,
  context: OpenRouterImageContext
): Promise<string | null> {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("data:image/")) return trimmed;
  const dataUri = extractDataUriFromString(trimmed);
  if (dataUri) return dataUri;
  const assetUrlMatch = trimmed.match(/^https?:\/\/[^/]*openrouter\.ai\/api\/v1\/assets\/([^?#]+)/i);
  if (assetUrlMatch?.[1]) {
    const assetPointer = assetUrlMatch[1];
    const resolved = await fetchOpenRouterAsset(
      context.apiKey,
      assetPointer,
      context.signal,
      context.visitedPointers
    );
    if (resolved) return resolved;
  }
  if (/^asset:\/\//i.test(trimmed)) {
    return await fetchOpenRouterAsset(context.apiKey, trimmed, context.signal, context.visitedPointers);
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return await fetchUrlToDataUri(trimmed, context.signal);
  }
  const normalized = trimmed.replace(/^[`\s]+|[`\s]+$/g, "").replace(/\s+/g, "");
  if (isLikelyBareBase64(normalized)) {
    return `data:image/png;base64,${normalized}`;
  }
  return null;
}

async function findImageInValue(value: any, context: OpenRouterImageContext): Promise<string | null> {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") {
    return await normalizeOpenRouterImageString(value, context);
  }
  if (typeof value !== "object") return null;
  if (context.visited.has(value)) return null;
  context.visited.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const result = await findImageInValue(item, context);
      if (result) return result;
    }
    return null;
  }

  const pointerFields = ["asset_pointer", "assetPointer"] as const;
  for (const field of pointerFields) {
    const pointer = (value as any)[field];
    if (typeof pointer === "string") {
      const resolved = await fetchOpenRouterAsset(context.apiKey, pointer, context.signal, context.visitedPointers);
      if (resolved) return resolved;
    }
  }

  const prioritizedKeys = [
    "image",
    "image_url",
    "imageUrl",
    "image_base64",
    "imageBase64",
    "b64_json",
    "b64Json",
    "base64",
    "data",
    "content",
    "output",
    "outputs",
    "result",
    "results",
    "response",
    "responses",
    "parts",
    "messages",
    "message",
    "choices",
    "url",
    "signed_url",
    "inline_data",
    "inlineData",
    "value",
  ];

  for (const key of prioritizedKeys) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      const result = await findImageInValue((value as any)[key], context);
      if (result) return result;
    }
  }

  for (const val of Object.values(value)) {
    const result = await findImageInValue(val, context);
    if (result) return result;
  }

  return null;
}

async function resolveOpenRouterImage(
  json: any,
  apiKey: string,
  signal?: AbortSignal
): Promise<string | null> {
  const messageImages = json?.choices?.[0]?.message?.images;
  if (Array.isArray(messageImages)) {
    for (const img of messageImages) {
      const url =
        typeof img?.image_url?.url === "string" ? img.image_url.url :
        typeof img?.url === "string" ? img.url :
        undefined;
      if (url) {
        const normalized = await normalizeOpenRouterImageString(url, {
          apiKey,
          signal,
          visited: new Set<any>(),
          visitedPointers: new Set<string>(),
        });
        if (normalized) return normalized;
      }
    }
  }

  const context: OpenRouterImageContext = {
    apiKey,
    signal,
    visited: new Set<any>(),
    visitedPointers: new Set<string>(),
  };
  const message = json?.choices?.[0]?.message;
  const candidates = [
    message?.content,
    message,
    json?.choices,
    json?.data,
    json?.output,
    json?.outputs,
    json,
  ];
  for (const candidate of candidates) {
    const result = await findImageInValue(candidate, context);
    if (result) return result;
  }
  return null;
}

function splitDataUriForOpenRouter(dataUri: string): { mimeType: string; base64: string } {
  const match = dataUri.match(/^data:([^;]+);base64,(.+)$/i);
  if (match && match[1] && match[2]) {
    return { mimeType: match[1], base64: match[2] };
  }
  const cleaned = dataUri.replace(/^data:image\/[^;]+;base64,/i, '');
  return { mimeType: 'image/png', base64: cleaned };
}

async function resolveReferenceImageForOpenRouter(
  value: string,
  apiKey: string,
  signal?: AbortSignal
): Promise<{ mimeType: string; base64: string }> {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    throw new Error('Reference image is required for OpenRouter image edit');
  }
  if (trimmed.startsWith('data:image/')) {
    return splitDataUriForOpenRouter(trimmed);
  }
  const dataUri = extractDataUriFromString(trimmed);
  if (dataUri) {
    return splitDataUriForOpenRouter(dataUri);
  }
  if (/^asset:\/\//i.test(trimmed)) {
    const assetDataUri = await fetchOpenRouterAsset(apiKey, trimmed, signal, new Set<string>());
    if (assetDataUri) {
      return splitDataUriForOpenRouter(assetDataUri);
    }
    throw new Error('Failed to resolve OpenRouter asset pointer for reference image');
  }
  if (/^https?:\/\//i.test(trimmed)) {
    const resolved = await fetchUrlToDataUri(trimmed, signal);
    if (resolved) {
      return splitDataUriForOpenRouter(resolved);
    }
    throw new Error('Failed to download reference image from provided URL');
  }
  const normalized = trimmed.replace(/^[`\s]+|[`\s]+$/g, '').replace(/\s+/g, '');
  if (isLikelyBareBase64(normalized)) {
    return { mimeType: 'image/png', base64: normalized };
  }
  throw new Error('Reference image must be a data URI, base64 string, asset pointer, or URL');
}

// AI Edit: returns a JSON Merge Patch object describing only the changed fields
export async function editCharacterOpenRouter(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<any>> {
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      { role: 'user', content: prompts.user },
    ],
  };
  const json = await postChatCompletions(api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  const parsed = parseJsonSafe<any>(content);
  return { value: parsed, usage };
}

function parseJsonSafe<T>(text: string): T {
  try {
    const cleaned = extractJsonObject(text);
    const parsed = JSON.parse(cleaned) as T;
    // Ensure we got a valid object
    if (!parsed || typeof parsed !== 'object') {
      console.error('[OpenRouter] Parsed result is not an object:', parsed);
      throw new Error('Invalid JSON response: parsed result is not an object');
    }
    return parsed;
  } catch (error) {
    console.error('[OpenRouter] JSON parsing failed:', { text, error });
    throw new Error(`Failed to parse AI response as JSON: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export function buildOpenRouterErrorMessage(status: number, payload: any, requestedModel?: string | null): string {
  let message = `HTTP ${status}`;
  let isRateLimit = status === 429;
  try {
    const errorPayload = payload?.error ?? payload;
    const metadata = (errorPayload && typeof errorPayload === 'object') ? errorPayload.metadata : undefined;
    const rawDetail = typeof metadata?.raw === 'string' ? metadata.raw : undefined;
    const upstreamProvider = typeof metadata?.provider_name === 'string'
      ? metadata.provider_name
      : typeof metadata?.provider === 'string'
        ? metadata.provider
        : undefined;
    if (status === 429) {
      const parts: string[] = [];
      if (upstreamProvider) parts.push(upstreamProvider);
      if (rawDetail) parts.push(rawDetail);
      const detail = parts.join(': ');
      message = detail
        ? `Rate limit exceeded – ${detail}`
        : 'Rate limit exceeded by upstream provider on OpenRouter';
      isRateLimit = true;
    } else {
      message = errorPayload?.message || message;
    }
    const serialized = JSON.stringify(payload || {});
    const text = `${message} ${serialized}`;
    if (/PROHIBITED_CONTENT/i.test(text) || /safety/i.test(text) || /policy violation/i.test(text) || /content is not allowed/i.test(text)) {
      throw new Error('Blocked by safety filters (provider policy). Please adjust your prompt (avoid explicit sexual content, minors, or graphic violence) and try again.');
    }
  } catch (error: any) {
    if (error instanceof Error && /Blocked by safety filters/.test(error.message)) {
      throw error;
    }
  }
  if (!/rate limit/i.test(message) && isRateLimit) {
    message = `Rate limit exceeded: ${message}`;
  }
  if (requestedModel) {
    const normalized = String(requestedModel).trim();
    if (normalized && !message.includes(normalized)) {
      message = `${message} (requested model: ${normalized})`;
    }
  }
  return message;
}

async function postChatCompletions(apiKey: string, payload: any, opts?: { signal?: AbortSignal }): Promise<any> {
  const resp = await fetch('https://openrouter.ai/api/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
      'HTTP-Referer': window.location.origin,
      'X-Title': appTitle,
    },
    body: JSON.stringify(payload),
    signal: opts?.signal,
  });
  if (!resp.ok) {
    const status = resp.status;
    let parsed: any = null;
    try {
      parsed = await resp.json();
    } catch {}
    const message = buildOpenRouterErrorMessage(status, parsed, payload?.model);
    throw new Error(message);
  }
  return resp.json();
}

export async function validateOpenRouterKey(config: { apiKey: string }): Promise<{ isValid: boolean; error?: string }> {
  try {
    const resp = await fetch('https://openrouter.ai/api/v1/models', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${config.apiKey}`,
        'HTTP-Referer': window.location.origin,
        'X-Title': appTitle,
      },
    });
    if (!resp.ok) {
      return { isValid: false, error: `HTTP ${resp.status}` };
    }
    const data = await resp.json();
    const ok = Array.isArray((data as any)?.data);
    return { isValid: ok };
  } catch (e: any) {
    return { isValid: false, error: String(e?.message || e) };
  }
}

export async function generateCharacterProfileOpenRouter(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<CharacterProfile>> {
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      { role: 'user', content: prompts.user },
    ],
  };
  const json = await postChatCompletions(api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = json?.choices?.[0]?.message?.content ?? '';

  if (!content) {
    console.error('[OpenRouter] Empty content in response:', json);
    throw new Error('OpenRouter returned empty content in response');
  }

  const parsed = parseJsonSafe<any>(String(content));
  console.log('[OpenRouter] Parsed character profile:', {
    hasCharacterProfile: 'character_profile' in parsed,
    hasCharacter: 'character' in parsed,
    topLevelKeys: Object.keys(parsed),
    name: parsed?.name,
  });

  const data = parsed?.character_profile || parsed?.character || parsed;

  if (!data || typeof data !== 'object') {
    console.error('[OpenRouter] Invalid data extracted:', { parsed, data });
    throw new Error('Failed to extract character data from response');
  }

  return { value: data as CharacterProfile, usage };
}

export async function generateChatExportFieldsOpenRouter(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<ChatExportFields>> {
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      { role: 'user', content: prompts.user },
    ],
  };
  const json = await postChatCompletions(api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = json?.choices?.[0]?.message?.content ?? '';
  return { value: parseJsonSafe<ChatExportFields>(String(content)), usage };
}

function parseImagePlanPromptsRobust(text: string): { naturalPrompt: string; stableDiffusionPrompt: string; negativePrompt?: string } {
  const content = String(text || '').trim();
  if (!content) throw new Error('Empty response from provider');
  try {
    return parseJsonLike<{ naturalPrompt: string; stableDiffusionPrompt: string; negativePrompt?: string }>(content);
  } catch {}
  const result: Partial<{ naturalPrompt: string; stableDiffusionPrompt: string; negativePrompt?: string }> = {};
  const lines = content.split(/\r?\n/);
  const tryExtract = (key: 'naturalPrompt' | 'stableDiffusionPrompt' | 'negativePrompt') => {
    for (const raw of lines) {
      const line = String(raw).trim();
      const re = new RegExp(`(^|[\\n\\-*•\\s])"?${key}"?\\s*[:=]\\s*(.+)$`, 'i');
      const m = line.match(re);
      if (m && m[2]) {
        let val = String(m[2]).trim();
        val = val.replace(/[`,]$/, '').replace(/^"|^'|^`/, '').replace(/"$|'$|`$/, '').trim();
        if (val) {
          (result as any)[key] = val;
          return;
        }
      }
    }
  };
  tryExtract('naturalPrompt');
  tryExtract('stableDiffusionPrompt');
  tryExtract('negativePrompt');
  if (result.naturalPrompt && !result.stableDiffusionPrompt) result.stableDiffusionPrompt = result.naturalPrompt;
  if (!result.naturalPrompt && result.stableDiffusionPrompt) result.naturalPrompt = result.stableDiffusionPrompt;
  if (result.naturalPrompt && result.stableDiffusionPrompt) {
    return result as any;
  }
  throw new Error('Provider returned non-JSON without extractable prompts');
}

export async function generateImagePlanPromptsOpenRouter(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<{ naturalPrompt: string; stableDiffusionPrompt: string; negativePrompt?: string }>> {
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      { role: 'user', content: prompts.user },
    ],
  };
  const json = await postChatCompletions(api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = json?.choices?.[0]?.message?.content ?? '';
  const value = parseImagePlanPromptsRobust(String(content));
  return { value, usage };
}

export async function rewriteCharacterFieldOpenRouter(
  instructions: string,
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<string>> {
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'user', content: instructions },
    ],
  };
  const json = await postChatCompletions(api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  const codeBlock = content.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  const value = (codeBlock ? codeBlock[1] : content).trim();
  return { value, usage };
}

// Roleplay: produce the next assistant message from system + chat history
export async function generateRoleplayReplyOpenRouter(
  prompts: { system: string; messages: Array<{ role: 'user' | 'assistant'; content: string }> },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<string>> {
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      ...prompts.messages.map((m) => ({ role: m.role, content: m.content })),
    ],
  };
  const json = await postChatCompletions(api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  return { value: content, usage };
}

// Image generation via chat/completions. Returns data URI string.
export async function generateImageOpenRouter(
  prompt: string,
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<{ imageData: string }>> {
  const payload = {
    model: api.model,
    messages: [{ role: 'user', content: prompt }],
    max_tokens: 1500,
    temperature: api.temperature ?? 0.7,
  };
  const json = await postChatCompletions(api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const resolvedImage = await resolveOpenRouterImage(json, api.apiKey, opts?.signal);
  if (resolvedImage) {
    return { value: { imageData: resolvedImage }, usage };
  }
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  if (content) {
    const direct = extractDataUriFromString(content);
    if (direct) {
      return { value: { imageData: direct }, usage };
    }
    const rawBase64 = content.replace(/^[\r\n\s`]+|[\r\n\s`]+$/g, '').replace(/\s+/g, '');
    if (/^[A-Za-z0-9+/=]+$/.test(rawBase64) && rawBase64.length > 100) {
      return { value: { imageData: `data:image/png;base64,${rawBase64}` }, usage };
    }
  }
  const fallback = await resolveOpenRouterImage(json?.choices?.[0], api.apiKey, opts?.signal);
  if (fallback) {
    return { value: { imageData: fallback }, usage };
  }
  const failureMessage = buildOpenRouterImageFailureMessage(
    json,
    'No image data URI produced by OpenRouter response'
  );
  throw new Error(failureMessage);
}

export async function generateImageEditOpenRouter(
  referenceImage: string | string[],
  instruction: string,
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<{ imageData: string }>> {
  // Handle array of reference images
  const referenceArray = Array.isArray(referenceImage) ? referenceImage : [referenceImage];
  const contentParts: Array<{ type: 'image_url'; image_url: { url: string } } | { type: 'text'; text: string }> = [];
  
  // Process all reference images
  for (const refImg of referenceArray) {
    const reference = await resolveReferenceImageForOpenRouter(refImg, api.apiKey, opts?.signal);
    const referenceDataUri = `data:${reference.mimeType};base64,${reference.base64}`;
    contentParts.push({
      type: 'image_url',
      image_url: { url: referenceDataUri },
    });
  }
  
  // Add instruction text at the end
  contentParts.push({ type: 'text', text: instruction });
  
  const payload = {
    model: api.model,
    messages: [
      {
        role: 'user' as const,
        content: contentParts,
      },
    ],
    max_tokens: 1500,
    temperature: api.temperature ?? 0.7,
  };
  const json = await postChatCompletions(api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const resolvedImage = await resolveOpenRouterImage(json, api.apiKey, opts?.signal);
  if (resolvedImage) {
    return { value: { imageData: resolvedImage }, usage };
  }
  const content = json?.choices?.[0]?.message?.content;
  if (Array.isArray(content)) {
    for (const part of content) {
      const maybe = typeof part?.image_url === 'string' ? part.image_url : undefined;
      if (maybe && maybe.startsWith('data:image/')) {
        return { value: { imageData: maybe }, usage };
      }
    }
  }
  const failureMessage = buildOpenRouterImageFailureMessage(
    json,
    'No image data URI produced by OpenRouter response'
  );
  throw new Error(failureMessage);
}
